/**
 * Aktionen am Nachweis (ADR 007): Menü der zulässigen Aktionen und der Dialog dazu.
 *
 * Liste und Detail führen den nächsten Schritt als Knopf (`hauptAktion`) – in der Zeile am
 * Ende, im Detail oben – und die übrigen Aktionen im Menü (Status-Chip bzw. "Weitere
 * Aktionen"). Beide teilen sich Menü und Dialog über diesen Hook, damit eine Aktion überall
 * gleich heißt und gleich abläuft.
 *
 * Was zulässig ist, liefert der Server je Nachweis (`Evidence.aktionen`, der nächste Schritt
 * zuerst) – hier wird nichts davon nachgebaut.
 */
import React, { useCallback, useState } from "react";
import {
  ArchiveOutlined as ArchiveIcon,
  ArrowForward as StatusChangeIcon,
  DeleteOutlined as DeleteIcon,
  EventOutlined as EventIcon,
  UnarchiveOutlined as UnarchiveIcon,
} from "@mui/icons-material";
import EvidenceUebergangDialog, { CostAccess } from "./EvidenceUebergangDialog";
import {
  Evidence,
  EvidenceAktion,
  EvidenceUebergang,
  PermitCostItem,
  ProcessingPath,
} from "../../../../types";
import { deletedDeadlinesNotice } from "../../../../utils/deadlines";
import {
  ActionsMenuItem,
  ActionsMenuList,
} from "../../../common/overlays/ActionsMenu";

/** Aktionen, die nicht den Ablauf betreffen, sondern ob der Nachweis noch gebraucht wird */
const KENNZEICHEN_AKTIONEN = ["archivieren", "wieder_aufnehmen"];

/**
 * Icon einer Aktion: Archivieren und Wiederaufnehmen sind ein Kennzeichen und haben eigene;
 * jeder Statuswechsel – vor wie zurück – trägt dasselbe, damit das Menü ruhig bleibt.
 */
const aktionIcon = (aktion: EvidenceAktion): React.ReactElement => {
  switch (aktion.aktion) {
    case "archivieren":
      return <ArchiveIcon fontSize="small" />;
    case "wieder_aufnehmen":
      return <UnarchiveIcon fontSize="small" />;
    default:
      return <StatusChangeIcon fontSize="small" />;
  }
};

/** Aktion ohne Statuswechsel nach vorn – im Menü nach dem Trennstrich */
const istNebenaktion = (aktion: EvidenceAktion): boolean =>
  !aktion.ziel_status ||
  aktion.aktion === "korrektur_anfordern" ||
  KENNZEICHEN_AKTIONEN.includes(aktion.aktion);

/** Der nächste Schritt im Ablauf, falls es einen gibt */
export const naechsterSchritt = (evidence: Evidence): EvidenceAktion | null => {
  const erste = evidence.aktionen[0];
  return erste && !istNebenaktion(erste) ? erste : null;
};

/**
 * Die Aktion für den Knopf: beim archivierten Nachweis das Wiederaufnehmen, sonst der nächste
 * Schritt im Ablauf. Liste und Detail zeigen denselben Knopf.
 */
export const hauptAktion = (evidence: Evidence): EvidenceAktion | null =>
  evidence.archiviert
    ? (evidence.aktionen.find(
        (aktion) => aktion.aktion === "wieder_aufnehmen",
      ) ?? null)
    : naechsterSchritt(evidence);

export interface MenuOptionen {
  /**
   * Ohne den Schritt, der als Knopf direkt daneben steht (`hauptAktion`) – im Zeilenmenü und
   * unter "Weitere Aktionen". Das Menü am Status-Chip hat keinen Knopf daneben und zeigt ihn.
   */
  ohneHauptaktion?: boolean;
  /** Zusätzlich "Löschen" am Ende – nur im Zeilenmenü der Nachweisliste */
  onLoeschen?: () => void;
  /** "Termin anlegen" als erste Aktion nach dem Trennstrich – nicht am archivierten Nachweis */
  onAddDeadline?: () => void;
}

interface MenuZustand extends MenuOptionen {
  evidence: Evidence;
  anchor: HTMLElement;
}

interface DialogZustand {
  evidence: Evidence;
  aktion: EvidenceAktion;
  /** Nur „Bearbeitungsweg ändern“: der schon gewählte neue Weg */
  targetPath?: ProcessingPath;
}

export function useEvidenceAktionen(
  onTransition: (
    evidenceId: string,
    data: EvidenceUebergang,
  ) => Promise<Evidence | null>,
  /** Manuelle Termine am Nachweis – das Archivieren nennt, wie viele mitgelöscht werden */
  manualDeadlineCount?: (evidenceId: string) => number,
  /** Kostenposition je Nachweis und das Recht daran – für Plan- und Istkosten im Dialog */
  costs?: {
    itemOf: (evidenceId: string) => PermitCostItem | undefined;
    access: CostAccess;
  },
) {
  const [menu, setMenu] = useState<MenuZustand | null>(null);
  const [dialog, setDialog] = useState<DialogZustand | null>(null);

  const oeffneMenu = useCallback(
    (evidence: Evidence, anchor: HTMLElement, optionen: MenuOptionen = {}) =>
      setMenu({ evidence, anchor, ...optionen }),
    [],
  );

  const oeffneAktion = useCallback(
    (evidence: Evidence, aktion: EvidenceAktion) => {
      setMenu(null);
      setDialog({ evidence, aktion });
    },
    [],
  );

  /**
   * Den Bearbeitungsweg wechseln – etwa aus der Auswahl im Detail. Solange der Nachweis offen
   * ist, geht nichts verloren, und der Wechsel geschieht sofort; danach fragt der Dialog nach, mit
   * dem Hinweis, was entfällt, und dem Kommentar für die Historie.
   */
  const changePath = useCallback(
    async (evidence: Evidence, path: ProcessingPath) => {
      const aktion = evidence.aktionen.find((a) => a.aktion === "change_path");
      if (!aktion || path === evidence.processing_path) return;
      // Ohne Pflichtkommentar geht nichts verloren (der Server sagt es an der Aktion).
      if (!aktion.kommentar_pflicht) {
        await onTransition(evidence.id, {
          aktion: "change_path",
          processing_path: path,
        });
        return;
      }
      setMenu(null);
      setDialog({ evidence, aktion, targetPath: path });
    },
    [onTransition],
  );

  const handleSubmit = useCallback(
    async (data: EvidenceUebergang): Promise<boolean> => {
      if (!dialog) return false;
      return (await onTransition(dialog.evidence.id, data)) !== null;
    },
    [dialog, onTransition],
  );

  // Oben die Schritte im Ablauf, nach dem Trennstrich die Nebenaktionen (Archivieren …) und –
  // ohne eigenen Trennstrich – Löschen.
  const naechster = menu ? naechsterSchritt(menu.evidence) : null;
  const knopf = menu?.ohneHauptaktion ? hauptAktion(menu.evidence) : null;
  const sichtbar = (menu?.evidence.aktionen ?? []).filter(
    (aktion) => aktion.aktion !== knopf?.aktion,
  );
  const ablauf = sichtbar.filter((aktion) => !istNebenaktion(aktion));
  const neben = sichtbar.filter(istNebenaktion);
  const addDeadline =
    menu && !menu.evidence.archiviert ? menu.onAddDeadline : undefined;

  const alsEintrag = (aktion: EvidenceAktion): ActionsMenuItem => {
    const istNaechster = aktion.aktion === naechster?.aktion;
    return {
      label: aktion.label,
      icon: aktionIcon(aktion),
      onClick: () => menu && oeffneAktion(menu.evidence, aktion),
      secondary: istNaechster ? "Nächster Schritt" : undefined,
      emphasized: istNaechster,
    };
  };
  const nachStrich: ActionsMenuItem[] = [
    ...(addDeadline
      ? [
          {
            label: "Termin anlegen",
            icon: <EventIcon fontSize="small" />,
            onClick: addDeadline,
          },
        ]
      : []),
    ...neben.map(alsEintrag),
  ];
  const onLoeschen = menu?.onLoeschen;
  const eintraege: ActionsMenuItem[] = [
    ...ablauf.map(alsEintrag),
    ...nachStrich.map((eintrag, index) => ({
      ...eintrag,
      dividerBefore: index === 0 && ablauf.length > 0,
    })),
    // Löschen steht immer hinter einem Strich, auch wenn darüber nur "Termin anlegen" steht
    ...(onLoeschen
      ? [
          {
            label: "Löschen",
            icon: <DeleteIcon fontSize="small" />,
            onClick: onLoeschen,
            dividerBefore:
              neben.length === 0 && (ablauf.length > 0 || !!addDeadline),
            danger: true,
          },
        ]
      : []),
  ];

  const elemente = (
    <>
      <ActionsMenuList
        items={eintraege}
        anchor={menu?.anchor ?? null}
        onClose={() => setMenu(null)}
        align="left"
      />

      <EvidenceUebergangDialog
        evidence={dialog?.evidence ?? null}
        aktion={dialog?.aktion ?? null}
        targetPath={dialog?.targetPath}
        label={dialog ? dialog.aktion.label : ""}
        onClose={() => setDialog(null)}
        onSubmit={handleSubmit}
        notice={
          dialog?.aktion.aktion === "archivieren" && manualDeadlineCount
            ? deletedDeadlinesNotice(manualDeadlineCount(dialog.evidence.id))
            : undefined
        }
        costItem={dialog ? costs?.itemOf(dialog.evidence.id) : undefined}
        costAccess={costs?.access ?? "none"}
      />
    </>
  );

  return { oeffneMenu, oeffneAktion, changePath, elemente };
}
